import React, { useEffect, useState } from "react";
import {
  Button,
  message,
  DatePicker,
  Space,
  Typography,
  Row,
  Col,
  Divider,
  Spin,
  Form,
  Empty,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  CopyOutlined,
} from "@ant-design/icons";
// import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import axios from "axios";
import { settings } from "../../../utils/config";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";

const { Text } = Typography;

const LogScreen = () => {
  // const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [logData, setLogData] = useState([]);
  const [logText, setLogText] = useState("");

  // API call function
  const fetchDailyLog = async (date) => {
    try {
      setLoading(true);
      const formattedDate = date ? dayjs(date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
      
      const baseUrl = process.env.REACT_APP_API_URL;
      const exportUrl = new URL(
        `${baseUrl}/api/v1/serviceconnect/view-daily-log?date=${formattedDate}`
      );

      const hasAccessToken = settings?.getCookie("access_token");
      const response = await axios.get(exportUrl.toString(), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${hasAccessToken}`,
        },
      });
      
      // Axios tự động parse JSON, không cần .json()
      const result = response.data;
      
      if (result.success && result.logs && Array.isArray(result.logs)) {
        setLogData(result.logs);
        
        // Logs đã ở dạng string format sẵn, chỉ cần join với newline
        const textLogs = result.logs.join('\n');
        
        setLogText(textLogs);
        message.success("Tải nhật ký thành công!");
      } else {
        setLogData([]);
        setLogText("");
        message.warning("Không có dữ liệu nhật ký cho ngày đã chọn!");
      }
    } catch (error) {
      console.error("Error fetching daily log:", error);
      
      // Xử lý lỗi chi tiết hơn
      if (error.response) {
        // Server trả về response với status code lỗi
        const status = error.response.status;
        const errorMessage = error.response.data?.message || 'Lỗi không xác định';
        
        if (status === 401) {
          message.error("Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!");
        } else if (status === 403) {
          message.error("Bạn không có quyền truy cập chức năng này!");
        } else if (status === 404) {
          message.error("Không tìm thấy dữ liệu nhật ký!");
        } else {
          message.error(`Lỗi ${status}: ${errorMessage}`);
        }
      } else if (error.request) {
        // Request được gửi nhưng không có response
        message.error("Không thể kết nối tới server. Vui lòng kiểm tra kết nối mạng!");
      } else {
        // Lỗi khác
        message.error("Không thể tải nhật ký. Vui lòng thử lại!");
      }
      
      setLogData([]);
      setLogText("");
    } finally {
      setLoading(false);
    }
  };

  // Load today's log on first render
  useEffect(() => {
    fetchDailyLog(dayjs());
    form.setFieldsValue({ logDate: dayjs() });
  }, []);

  const handleSearchLog = async (values) => {
    const { logDate } = values;
    await fetchDailyLog(logDate);
  };

  const handleRefresh = () => {
    const currentDate = form.getFieldValue('logDate') || dayjs();
    fetchDailyLog(currentDate);
  };

  const handleCopyLog = () => {
    if (logText) {
      navigator.clipboard.writeText(logText).then(() => {
        message.success("Đã sao chép log vào clipboard!");
      }).catch(() => {
        message.error("Không thể sao chép log!");
      });
    }
  };

  return (
    <BrandedPageLayout
      title="Nhật ký hệ thống"
      subtitle="Theo dõi và truy xuất lịch sử hoạt động của toàn bộ hệ thống để đảm bảo tính minh bạch."
      breadcrumbItems={[{ title: 'Hệ thống', path: '/logs' }, { title: 'Nhật ký' }]}
    >

        <Divider style={{ margin: "16px 0" }} />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearchLog}
          initialValues={{ logDate: dayjs() }}
        >
          <Row gutter={24} align="bottom">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item
                name="logDate"
                // label={
                //   <Space>
                //     <CalendarOutlined />
                //     <span>Chọn ngày xem log</span>
                //   </Space>
                // }
                rules={[{ required: true, message: "Vui lòng chọn ngày!" }]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  placeholder="Chọn ngày"
                  format="DD/MM/YYYY"
                  allowClear={false}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={16} lg={18}>
              <Space style={{ width: "100%", justifyContent: "flex-end", flexWrap: "wrap" }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SearchOutlined />}
                >
                  Xem log
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleRefresh}
                  loading={loading}
                >
                  Làm mới
                </Button>
                <Button
                  icon={<CopyOutlined />}
                  onClick={handleCopyLog}
                  disabled={!logText}
                >
                  Sao chép
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>

        <Divider style={{ margin: "16px 0" }} />

        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text strong style={{ fontSize: 16 }}>
              Nhật ký hệ thống {form.getFieldValue('logDate')?.format('DD/MM/YYYY')}
            </Text>
            <Text type="secondary">
              Tổng: {logData.length} bản ghi
            </Text>
          </div>

          <Spin spinning={loading} tip="Đang tải nhật ký...">
            <div
              style={{
                backgroundColor: '#f5f5f5',
                border: '1px solid #d9d9d9',
                borderRadius: 6,
                padding: 16,
                minHeight: 400,
                maxHeight: 600,
                overflowY: 'auto',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: 13,
                lineHeight: 1.4,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {logText ? (
                <Text style={{ color: '#333' }}>
                  {logText}
                </Text>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Không có dữ liệu nhật ký"
                  style={{ margin: '60px 0' }}
                />
              )}
            </div>
          </Spin>
        </div>
    </BrandedPageLayout>
  );
};

export default LogScreen;